import type { RequestFn } from '../http';
import type {
  CreateInventorySessionInput,
  InventoryCWDraftResult,
  InventoryProductSearchResult,
  InventoryReviewLine,
  InventorySessionDetail,
  InventorySessionListItem,
  InventorySessionStatus,
  UpsertInventoryLineInput,
} from '../types';

export function createInventorySessionsApi(request: RequestFn) {
  return {
    async getInventorySessions() {
      return request<{ sessions: InventorySessionListItem[] }>('/inventory/sessions');
    },

    async createInventorySession(data: CreateInventorySessionInput) {
      return request<{ session: { id: string; status: InventorySessionStatus } }>(
        '/inventory/sessions',
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );
    },

    async getInventorySession(sessionId: string) {
      return request<{ session: InventorySessionDetail }>(`/inventory/sessions/${sessionId}`);
    },

    async startInventorySession(sessionId: string) {
      return request<{ session: { id: string; status: InventorySessionStatus } }>(
        `/inventory/sessions/${sessionId}/start`,
        { method: 'POST' }
      );
    },

    async setInventorySessionReview(sessionId: string) {
      return request<{ session: { id: string; status: InventorySessionStatus } }>(
        `/inventory/sessions/${sessionId}/review`,
        { method: 'POST' }
      );
    },

    async cancelInventorySession(sessionId: string) {
      return request<{ success: boolean }>(`/inventory/sessions/${sessionId}`, {
        method: 'DELETE',
      });
    },

    async upsertInventoryLine(sessionId: string, data: UpsertInventoryLineInput) {
      return request<{ line: { id: string; countedQty: number } }>(
        `/inventory/sessions/${sessionId}/lines`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );
    },

    async removeInventoryLine(sessionId: string, lineId: string) {
      return request<{ success: boolean }>(
        `/inventory/sessions/${sessionId}/lines/${lineId}`,
        {
          method: 'DELETE',
        }
      );
    },

    async searchInventoryProducts(q: string) {
      return request<{ products: InventoryProductSearchResult[] }>(
        `/inventory/products/search?q=${encodeURIComponent(q)}`
      );
    },

    async getInventoryReviewData(sessionId: string) {
      return request<{ lines: InventoryReviewLine[] }>(
        `/inventory/sessions/${sessionId}/review`
      );
    },

    async saveInventoryCWDraft(sessionId: string) {
      return request<InventoryCWDraftResult>(`/inventory/sessions/${sessionId}/cw-draft`, {
        method: 'POST',
      });
    },

    async finalizeInventoryCW(sessionId: string) {
      return request<{ success: boolean; message: string }>(
        `/inventory/sessions/${sessionId}/cw-finalize`,
        { method: 'POST' }
      );
    },

    async retryInventorySessionPdf(sessionId: string): Promise<{ cwPdfUploaded: boolean }> {
      return request<{ cwPdfUploaded: boolean }>(`/inventory/sessions/${sessionId}/retry-pdf`, { method: 'POST' });
    },

    async downloadInventorySessionPdf(sessionId: string): Promise<void> {
      const res = await fetch(`/api/inventory/sessions/${sessionId}/pdf`, { credentials: 'include' });
      if (!res.ok) {
        let msg = 'PDF konnte nicht geladen werden';
        try { const j = await res.json() as { error?: string }; msg = j.error ?? msg; } catch { /* ignore */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    },
  };
}
